import React, { useState, useContext } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { StagedProduct } from '@/stores/stagedProducts/stagedProducts.types';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { useIsTouch } from '@/hooks/use-touch';
import { useIsMobile } from '@/hooks/use-mobile';
import NumericKeyboard from '@/components/NumericKeyboard';
import { SheetPortal } from '@/components/ui/sheet';

interface CustomerPositionModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  stagedProduct: StagedProduct | null;
  onSave: (lineId: string, customerPosition: number | undefined) => void;
}

export const CustomerPositionModal: React.FC<CustomerPositionModalProps> = ({
  isOpen,
  onOpenChange,
  stagedProduct,
  onSave,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const isTouch = useIsTouch();
  const isMobile = useIsMobile();
  const useCustomNumpad = isTouch && !isMobile;
  const [localPosition, setLocalPosition] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevLineId, setPrevLineId] = useState<string | undefined | null>(stagedProduct?.lineId);

  if (isOpen !== prevIsOpen || stagedProduct?.lineId !== prevLineId) {
    setPrevIsOpen(isOpen);
    setPrevLineId(stagedProduct?.lineId);
    if (isOpen && stagedProduct) {
      setLocalPosition(
        stagedProduct.customerPosition != null ? String(stagedProduct.customerPosition) : '',
      );
      setShowKeyboard(false);
    } else {
      setShowKeyboard(false);
    }
  }

  if (!stagedProduct) return null;

  const handlePositionChange = (value: string) => {
    if (!/^\d*$/.test(value)) return;
    setLocalPosition(value);
  };

  const handleSave = () => {
    const parsed = parseInt(localPosition, 10);
    if (!localPosition.trim() || isNaN(parsed) || parsed <= 0) {
      onSave(stagedProduct.lineId, undefined);
    } else {
      onSave(stagedProduct.lineId, parsed);
    }
    setShowKeyboard(false);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setShowKeyboard(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent
        {...(useCustomNumpad ? { onOpenAutoFocus: (e) => e.preventDefault() } : {})}
        className="grid w-80 max-w-[calc(100vw-2rem)] grid-rows-[auto_1fr_auto] gap-6 p-4"
   
      >
        <DialogHeader className="pr-10 text-left space-y-0 gap-1">
          <DialogTitle className="text-2xl">{translator('ProductsCatalog.customerPositionTitle')}</DialogTitle>
          <DialogDescription className="line-clamp-2 break-words">
            {stagedProduct.product.name}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center">
          <Input
            type="text"
            inputMode={useCustomNumpad ? 'none' : 'numeric'}
            readOnly={useCustomNumpad}
            hideSpinners
            showcustomNumPadKeyboard={false}
            value={localPosition}
            onChange={(e) => handlePositionChange(e.target.value)}
            onClick={() => useCustomNumpad && setShowKeyboard(true)}
            autoFocus={!useCustomNumpad}
            className="w-16 text-center"
          />
        </div>

        <DialogFooter>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            {translator('Global.cancel')}
          </Button>
          <Button size="sm" onClick={handleSave}>
            {translator('Global.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
      {useCustomNumpad && showKeyboard && (
        <SheetPortal>
          <div className="fixed inset-0 z-50 pointer-events-none">
            <div className="absolute bottom-20 right-0 w-80 h-auto max-h-[60vh] p-0 flex flex-col border-t border-l rounded-tl-lg bg-background shadow-lg pointer-events-auto">
              <NumericKeyboard
                value={localPosition}
                onChange={handlePositionChange}
                onConfirm={handleSave}
                onCancel={() => setShowKeyboard(false)}
                translator={translator}
                title={translator('ProductsCatalog.customerPositionTitle')}
              />
            </div>
          </div>
        </SheetPortal>
      )}
    </Dialog>
  );
};